import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';
import { SidenavContentComponent } from './shared/sidenav-content/sidenav-content.component';

import { SigninComponent } from './signin/signin.component';
import { ViewSlabsComponent } from './view-slabs/view-slabs.component';
import { ViewIncentivesComponent } from './view-incentives/view-incentives.component';
import { IAllocatorIncentiveComponent } from './i-allocator-incentive/i-allocator-incentive.component';
import { CreateIncentiveComponent } from './create-incentive/create-incentive.component';
import { AuthGuardService } from './auth-guard.service';
import { PenaltyRewardCampaignComponent } from './penalty-reward-campaign/penalty-reward-campaign.component';
import { ViewCampaignsComponent } from './view-campaigns/view-campaigns.component';
import { ViewIncentiveComponent } from './view-incentive/view-incentive.component';
import { BulkPayInitiateComponent } from './bulkpay/initiate/initiate.component';
import { BulkPayHistoryComponent } from './bulkpay/history/history.component';
import { AUDIT_CAMPAIGN_INITIATE, BULK_PAY_APPROVALS, BULK_PAY_HISTORY, BULK_PAY_INITIATE, CREATE_SUBSCRIPTION, LIST_ACCESS_FEE_PLANS, LIST_BILLING_PLAN_REQUESTS, LIST_SUBSCRIPTION_REQUESTS, LIST_SUBSCRIPTIONS, VIEW_ACCESS_FEE_PLAN, VIEW_BILLING_PLAN_REQUEST, VIEW_SUBSCRIPTION, VIEW_SUBSCRIPTION_REQUEST, BULK_CREATE_SUBSCRIPTION, CREATE_SHIFT, CREATE_SLAB, CREATE_GEO_SLAB, VIEW_SLAB, DUPLICATE_SLAB, LIST_REDEEMS, CREATE_REDEEM, EDIT_REDEEM, CREATE_LIMIT_STORE, LIST_LIMIT_STORE, LIST_LIMIT_APPROVALS } from './routes';
import { RedeemListComponent } from './redeem/list/redeem-list.component';
import { RedeemCreateComponent } from './redeem/create/redeem-create.component';
import { LimitStoreCreateComponent } from './limit-store/create/limit-store-create.component';
import { LimitStoreListComponent } from './limit-store/list/limit-store-list.component';
import { MakerCheckerApprovalsComponent } from './shared/maker-checker/maker-checker-approvals.component';
import { SubscriptionComponent } from './subscription/subscription.component';
import { BulkCreateSubscriptionComponent } from './subscription/bulk-subscription/bulk.subscription.create.component';
import { AuditCampaignInitiateComponent } from './penalty-reward-campaign/audit-campaign/audit-campaign.component';
import { IAllocatorConstructUploadComponent } from './i-allocator-incentive/construct-upload/construct-upload.component';
import { ListSubscriptionsComponent } from './subscription/prepaid/list/list.component';
import { ViewSubscriptionRuleComponent } from './subscription/prepaid/view/view.component';
import { ViewAccessFeeComponent } from './subscription/accessFee/view/view.component';
import { ListAccessFeeComponent } from './subscription/accessFee/list/accessFee.list.component';
import { ConfigRequestListComponent } from './subscription/config-requests/list.component';
import { ConfigRequestViewComponent } from './subscription/config-requests/view.component';
import { CreateShiftComponent } from './shift/create-shift/create-shift.component';
import { CreateSlabComponent } from './create-slab/create-slab.component';
import { AppComponent } from './app.component';
import { IncentiveApprovalRequestsComponent } from './incentive-approval-requests/incentive-approval-requests.component';
import { INCENTIVE_APPROVAL_REQUESTS, SLAB_APPROVAL_REQUESTS } from './routes';
import { AUTHZ_FEATURES } from './constants/permissions';
import { SlabApprovalRequestsComponent } from './slab-approval-requests/slab-approval-requests.component';
import { HomeComponent } from './home/home.component';

const routes: Routes = [
  {
    path: 'login',
    component: SigninComponent
  },
  {
    path: '',
    canActivate: [AuthGuardService],
    component: SidenavContentComponent,
    children: [
      {
        path: '',
        pathMatch: 'full',
        component: HomeComponent,
      },
      {
        path: 'createIncentive',
        canActivate: [AuthGuardService],
        component: CreateIncentiveComponent,
        data: { permissionActions: ['edit'], feature: AUTHZ_FEATURES.INCENTIVE }
      },
      {
        path: 'slabs',
        canActivate: [AuthGuardService],
        component: ViewSlabsComponent,
        data: { permissionActions: ['view'], feature: AUTHZ_FEATURES.SLAB }
      },
      {
        path: SLAB_APPROVAL_REQUESTS,
        canActivate: [AuthGuardService],
        component: SlabApprovalRequestsComponent,
        data: {
          permissionActions: ['view', 'create', 'approve'],
          feature: AUTHZ_FEATURES.SLAB
        }
      },
      {
        path: `${SLAB_APPROVAL_REQUESTS}/:requestId`,
        canActivate: [AuthGuardService],
        component: CreateSlabComponent,
        data: {
          permissionActions: ['view', 'create', 'approve'],
          feature: AUTHZ_FEATURES.SLAB
        }
      },
      {
        path: 'incentives',
        canActivate: [AuthGuardService],
        component: ViewIncentivesComponent,
        data: {
          permissionActions: ['view', 'edit', 'approve'],
          feature: AUTHZ_FEATURES.INCENTIVE,
        }
      },
      {
        path: INCENTIVE_APPROVAL_REQUESTS,
        canActivate: [AuthGuardService],
        component: IncentiveApprovalRequestsComponent,
        data: {
          permissionActions: ['view', 'edit', 'approve'],
          feature: AUTHZ_FEATURES.INCENTIVE,
        }
      },
      {
        path: `${INCENTIVE_APPROVAL_REQUESTS}/:incentiveId`,
        canActivate: [AuthGuardService],
        component: ViewIncentiveComponent,
        data: {
          permissionActions: ['view', 'edit', 'approve'],
          feature: AUTHZ_FEATURES.INCENTIVE,
        }
      },
      {
        path: 'viewIncentive/:incentiveId/:action',
        canActivate: [AuthGuardService],
        component: ViewIncentiveComponent,
        runGuardsAndResolvers: 'always',
        data: {
          permissionActions: ['view', 'edit', 'approve'],
          feature: AUTHZ_FEATURES.INCENTIVE,
        },
      },
      {
        path: 'incentive/:incentiveId/:action',
        canActivate: [AuthGuardService],
        component: CreateIncentiveComponent,
        runGuardsAndResolvers: 'always',
        data: { permissionActions: ['edit'], feature: AUTHZ_FEATURES.INCENTIVE },
      },
      {
        path: 'penalty-reward-campaign',
        children: [{
          path: 'create',
          component: PenaltyRewardCampaignComponent,
        }],
        canActivate: [AuthGuardService],
        data: { permission: 'edit_penalty_reward' }
      },
      {
        path: 'campaigns',
        component: ViewCampaignsComponent,
        canActivate: [AuthGuardService],
        data: { permission: 'view_penalty_reward' }
      },
      {
        path: 'newIncentive',
        canActivate: [AuthGuardService],
        component: IAllocatorIncentiveComponent,
        data: { permissionActions: ['edit'], feature: AUTHZ_FEATURES.INCENTIVE }
      },
      {
        path: 'uploadConstruct',
        canActivate: [AuthGuardService],
        component: IAllocatorConstructUploadComponent,
        data: { permission: 'iallocator_construct_upload' }
      },
      {
        path: 'campaign/:campaignId/:status/:action',
        component: PenaltyRewardCampaignComponent,
        data: { permission: 'view_penalty_reward' }
      },
      {
        path: AUDIT_CAMPAIGN_INITIATE,
        canActivate: [AuthGuardService],
        component: AuditCampaignInitiateComponent,
        data: { permission: 'edit_audit_campaign' }
      },
      {
        path: BULK_PAY_INITIATE,
        canActivate: [AuthGuardService],
        component: BulkPayInitiateComponent,
        data: { permissionActions: ['create'], feature: AUTHZ_FEATURES.BULK_PAY }
      },
      {
        path: BULK_PAY_HISTORY,
        canActivate: [AuthGuardService],
        component: BulkPayHistoryComponent,
        data: { permissionActions: ['view'], feature: AUTHZ_FEATURES.BULK_PAY }
      },
      {
        path: BULK_PAY_APPROVALS,
        canActivate: [AuthGuardService],
        component: MakerCheckerApprovalsComponent,
        data: {
          permissionActions: ['view', 'create', 'approve'],
          feature: AUTHZ_FEATURES.BULK_PAY,
          entityType: 'BULK_PAY',
          title: 'Bulk Payment Approvals',
          changeLabel: 'bulk payment',
          approvalsApi: '/api/bulkpay/approvals',
          payloadFields: [
            { key: 'subTransactionType', label: 'Type' },
            { key: 'remarks', label: 'Remarks' },
            { key: 'comment', label: 'Comment' },
            { key: 'transactions', label: 'Rows' }
          ]
        }
      },
      {
        path: CREATE_SUBSCRIPTION,
        canActivate: [AuthGuardService],
        component: SubscriptionComponent,
        data: { permissionActions: ['create'], feature: AUTHZ_FEATURES.SUBSCRIPTION }
      },
      {
        path: LIST_SUBSCRIPTIONS,
        canActivate: [AuthGuardService],
        component: ListSubscriptionsComponent,
        data: { permissionActions: ['view'], feature: AUTHZ_FEATURES.SUBSCRIPTION }
      },
      {
        path: LIST_ACCESS_FEE_PLANS,
        canActivate: [AuthGuardService],
        component: ListAccessFeeComponent,
        data: { permissionActions: ['view'], feature: AUTHZ_FEATURES.BILLING_PLANS }
      },
      {
        path: VIEW_SUBSCRIPTION,
        canActivate: [AuthGuardService],
        component: ViewSubscriptionRuleComponent,
        data: { permissionActions: ['view'], feature: AUTHZ_FEATURES.SUBSCRIPTION }
      },
      {
        path: VIEW_ACCESS_FEE_PLAN,
        canActivate: [AuthGuardService],
        component: ViewAccessFeeComponent,
        data: { permissionActions: ['view'], feature: AUTHZ_FEATURES.BILLING_PLANS }
      },
      {
        path: LIST_SUBSCRIPTION_REQUESTS,
        canActivate: [AuthGuardService],
        component: ConfigRequestListComponent,
        data: {
          kind: 'subscription',
          title: 'Subscription Requests',
          permissionActions: ['view', 'approve'],
          feature: AUTHZ_FEATURES.SUBSCRIPTION,
          allowWhenPanelOff: true,
        }
      },
      {
        path: VIEW_SUBSCRIPTION_REQUEST,
        canActivate: [AuthGuardService],
        component: ConfigRequestViewComponent,
        data: {
          kind: 'subscription',
          permissionActions: ['view', 'approve'],
          feature: AUTHZ_FEATURES.SUBSCRIPTION,
          allowWhenPanelOff: true,
        }
      },
      {
        path: LIST_BILLING_PLAN_REQUESTS,
        canActivate: [AuthGuardService],
        component: ConfigRequestListComponent,
        data: {
          kind: 'billingPlan',
          title: 'Access Fee Requests',
          permissionActions: ['view', 'approve'],
          feature: AUTHZ_FEATURES.BILLING_PLANS,
          allowWhenPanelOff: true,
        }
      },
      {
        path: VIEW_BILLING_PLAN_REQUEST,
        canActivate: [AuthGuardService],
        component: ConfigRequestViewComponent,
        data: {
          kind: 'billingPlan',
          permissionActions: ['view', 'approve'],
          feature: AUTHZ_FEATURES.BILLING_PLANS,
          allowWhenPanelOff: true,
        }
      },
      {
        path: BULK_CREATE_SUBSCRIPTION,
        canActivate: [AuthGuardService],
        component: BulkCreateSubscriptionComponent,
        data: { permissionActions: ['create'], feature: AUTHZ_FEATURES.SUBSCRIPTION }
      },
      {
        path: CREATE_SHIFT,
        canActivate: [AuthGuardService],
        component: CreateShiftComponent,
        data: { permissionActions: ['create'], feature: AUTHZ_FEATURES.SLAB }
      },
      {
        path: CREATE_SLAB,
        canActivate: [AuthGuardService],
        component: CreateSlabComponent,
        data: { permissionActions: ['create'], feature: AUTHZ_FEATURES.SLAB }
      },
      {
        path: CREATE_GEO_SLAB,
        canActivate: [AuthGuardService],
        component: CreateSlabComponent,
        data: { permissionActions: ['createGeoSlab'], geoSlab: true, feature: AUTHZ_FEATURES.SLAB }
      },
      {
        path: VIEW_SLAB,
        canActivate: [AuthGuardService],
        component: CreateSlabComponent,
        runGuardsAndResolvers: 'always',
        data: { permissionActions: ['view'], feature: AUTHZ_FEATURES.SLAB }
      },
      {
        path: DUPLICATE_SLAB,
        canActivate: [AuthGuardService],
        component: CreateSlabComponent,
        data: { permissionActions: ['create'], feature: AUTHZ_FEATURES.SLAB },
      },
      {
        path: LIST_REDEEMS,
        canActivate: [AuthGuardService],
        component: RedeemListComponent,
        data: { permission: 'redeem_list' }
      },
      {
        path: CREATE_REDEEM,
        canActivate: [AuthGuardService],
        component: RedeemCreateComponent,
        data: { permission: 'redeem_add' }
      },
      {
        path: EDIT_REDEEM,
        canActivate: [AuthGuardService],
        component: RedeemCreateComponent,
        runGuardsAndResolvers: 'always',
        data: { permission: 'redeem_edit' }
      },
      {
        path: LIST_LIMIT_STORE,
        canActivate: [AuthGuardService],
        component: LimitStoreListComponent,
        data: {
          permissionActions: ['view', 'edit', 'approve'],
          feature: AUTHZ_FEATURES.LIMIT_STORE,
        }
      },
      {
        path: LIST_LIMIT_APPROVALS,
        canActivate: [AuthGuardService],
        component: MakerCheckerApprovalsComponent,
        data: {
          permissionActions: ['view', 'edit', 'approve', 'approvalView'],
          feature: AUTHZ_FEATURES.LIMIT_STORE,
          entityType: 'LIMIT',
          title: 'Limit Approvals',
          payloadFields: [
            { key: 'city', label: 'City' },
            { key: 'identifier', label: 'Identifier' },
            { key: 'identifierId', label: 'Name' },
            { key: 'limitType', label: 'Limit type' },
            { key: 'limit', label: 'Limit' },
            { key: 'warning', label: 'Warning' },
            { key: 'priority', label: 'Priority' },
            { key: 'active', label: 'Active' }
          ]
        }
      },
      {
        path: CREATE_LIMIT_STORE,
        canActivate: [AuthGuardService],
        component: LimitStoreCreateComponent,
        data: { permissionActions: ['edit'], feature: AUTHZ_FEATURES.LIMIT_STORE }
      }
    ]
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule]
})
export class AppRoutingModule { }
